import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, Star, ArrowUpRight, Wallet, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { ideasStore } from "@/lib/storage";
import { OverallScore, ScoreBars } from "@/components/Scores";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-sm text-slate-200 mt-1 font-medium">{value || "—"}</div>
  </div>
);

export const SaveButtons = ({ idea, prefix }) => {
  const toggleSave = () => {
    ideasStore.update(idea.id, { saved: !idea.saved, favorite: idea.saved ? false : idea.favorite });
    toast.success(idea.saved ? "Idea rimossa dall'archivio" : "Idea salvata in Le mie idee");
  };
  const toggleFav = () => ideasStore.update(idea.id, { favorite: !idea.favorite, saved: true });
  return (
    <div className="flex gap-2">
      <button onClick={toggleSave} data-testid={`${prefix}-save-btn`} title="Salva"
        className={`p-2.5 rounded-full border transition-colors duration-200 ${idea.saved ? "bg-amber-500 text-slate-950 border-amber-500" : "border-white/10 text-slate-300 hover:border-amber-500/50 hover:text-amber-300"}`}>
        {idea.saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
      </button>
      <button onClick={toggleFav} data-testid={`${prefix}-favorite-btn`} title="Preferito"
        className={`p-2.5 rounded-full border transition-colors duration-200 ${idea.favorite ? "border-amber-500/60 text-amber-400 bg-amber-500/10" : "border-white/10 text-slate-300 hover:text-amber-300"}`}>
        <Star className="w-4 h-4" fill={idea.favorite ? "currentColor" : "none"} />
      </button>
    </div>
  );
};

export const IdeaCard = ({ idea, index = 0, testId }) => (
  <article
    data-testid={testId}
    style={{ animationDelay: `${index * 120}ms` }}
    className="fade-up flex flex-col rounded-2xl bg-[#12141F] border border-white/[0.07] p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_10px_30px_-10px_rgba(245,158,11,0.25)]"
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-2.5 py-0.5">
          {idea.categoria || "Idea"}
        </span>
        <h3 className="font-display text-xl font-bold text-slate-50 mt-3 leading-tight" data-testid={`${testId}-name`}>{idea.nome}</h3>
      </div>
      <OverallScore value={idea.punteggio_complessivo} testId={`${testId}-overall`} />
    </div>
    <p className="text-sm text-slate-300 mt-3 leading-relaxed">{idea.pitch}</p>
    <div className="mt-4 space-y-2 text-sm">
      <p><span className="text-slate-500">Problema: </span><span className="text-slate-300">{idea.problema}</span></p>
      <p><span className="text-slate-500">Cliente ideale: </span><span className="text-slate-300">{idea.cliente_ideale}</span></p>
    </div>
    <div className="flex flex-wrap gap-1.5 mt-4">
      {(idea.modelli_guadagno || []).map((m) => (
        <span key={m.tipo + (m.dettaglio || "")} className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-0.5">{m.tipo}</span>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-2 mt-4">
      <Fact icon={Wallet} label="Costi avvio" value={idea.costi_avvio} />
      <Fact icon={Clock} label="Primo guadagno" value={idea.tempo_primo_guadagno} />
    </div>
    <div className="mt-2"><Fact icon={TrendingUp} label="Potenziale" value={idea.guadagno_potenziale} /></div>
    <div className="mt-5"><ScoreBars scores={idea.punteggi} testPrefix={testId} /></div>
    <p className="mt-5 text-sm italic text-slate-400 border-l-2 border-amber-500/60 pl-3">{idea.giudizio}</p>
    <div className="mt-auto pt-6 flex items-center justify-between gap-3">
      <Link to={`/idea/${idea.id}`} data-testid={`${testId}-plan-btn`}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/10 px-4 py-2.5 text-sm font-semibold text-slate-100 transition-colors duration-200 hover:bg-amber-500 hover:text-slate-950 hover:border-amber-500">
        Piano 30 giorni <ArrowUpRight className="w-4 h-4" />
      </Link>
      <SaveButtons idea={idea} prefix={testId} />
    </div>
  </article>
);
